import axios from "axios";
import React, { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { Discount_Master_store_Brands, discountGroupObjects, DISOUNT_MASTER_CHANNEL_OPTIONS } from "./data";
import { GlobalState } from "context/GlobalContext";
import BrandChannel from "./BrandChannel/BrandChannel";
import ApplicableDate from "./ApplicableDateSection/ApplicableDate";
import DiscountValueSection from "./DiscountValueSection/DiscountValueSection";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Step,
	StepLabel,
	Stepper,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import MasterGeneralInformation from "./MasterGeneralInformation/MasterGeneralInformation";
import ProductSelectionSection from "./ProductSelectionSection/ProductSelectionSection";
import StoreAvailabilitySection from "./StoreAvailabilitySection/StoreAvailabilitySection";
import DiscountOverview from "./DiscountOverview/DiscountOverview";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import FinalSummary from "./summary/summary";
import { useLocation, useNavigate } from "react-router-dom";
import { generateSchemeStr, roleAccess } from "./DiscountMasterList";
import { getStoreDisplayName, getStoreMatchValues, normalizeValue } from "./DiscountSummary";

export function toMySQLDateTime(date) {
  const pad = (n) => n.toString().padStart(2, "0");

  return (
    date.getFullYear() + "-" +
    pad(date.getMonth() + 1) + "-" +
    pad(date.getDate()) + " " +
    pad(date.getHours()) + ":" +
    pad(date.getMinutes()) + ":" +
    pad(date.getSeconds())
  );
}

const jsDate =(date) =>{
	return new Date(date.replace(" ", "T") + "+05:30");
} 

const getEndOfDayDate = (value) => {
	if (!value) return value;

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;

	date.setHours(23, 59, 59, 0);
	return date;
};

const getSelectedStoreCodesFromStoreDetails = (selectedStores = [], storeDetails = []) => {
	const normalizedSelectedStores = [
		...new Set(
			selectedStores
				.map((store) => (typeof store === "object" ? getStoreDisplayName(store) : store))
				.map(normalizeValue)
				.filter(Boolean)
		),
	];

	if (!normalizedSelectedStores.length) return [];

	return [
		...new Set(
			storeDetails
				.filter((store) => {
					const storeName = normalizeValue(getStoreDisplayName(store));
					return normalizedSelectedStores.includes(storeName);
				})
				.map(
					(store) =>
						store?.site_code ||
						store?.siteCode ||
						store?.sitecode ||
						store?.store_code ||
						store?.storeCode ||
						store?.code ||
						""
				)
				.map((value) => String(value || "").trim())
				.filter(Boolean)
		),
	];
};

const arePrimitiveArraysEqual = (first = [], second = []) => {
	if (first === second) return true;
	if (first.length !== second.length) return false;

	const remaining = new Map();
	second.forEach((item) => remaining.set(item, (remaining.get(item) || 0) + 1));

	return first.every((item) => {
		const count = remaining.get(item) || 0;
		if (!count) return false;
		remaining.set(item, count - 1);
		return true;
	});
};

const snapshotStoreAvailability = (store = {}) => ({
	partner: [...(store.partner || [])],
	distributor: [...(store.distributor || [])],
	zone: [...(store.zone || [])],
	state: [...(store.state || [])],
	city: [...(store.city || [])],
	store_grade: [...(store.store_grade || [])],
	store: [...(store.store || [])],
});

const buildDiscountValueLookup = (discountValues = []) =>
	new Map(
		(discountValues || []).map((discount, index) => [
			discount?.id || `${discount?.scheme_type || "discount"}_${index + 1}`,
			discount,
		])
	);

const serializeFinalSummary = (summaryRows = []) =>
	(summaryRows || []).map((row) => {
		const { discount_value, ...restRow } = row || {};
		return restRow;
	});

const getInitialState = () => ({
	[discountGroupObjects.brand_channel.key]: {
		store_brand: [],
		channel: "",
	},
	[discountGroupObjects.general_information.key]: {
		scheme_type: "",
		scheme_name: "",
	},
	[discountGroupObjects.applicable_date.key]: {
		start_date: "",
		end_date: "",
		applicable_days: [],
	},
	[discountGroupObjects.store_availability.key]: {
		partner: [],
		zone: [],
		distributor: [],
		store: [],
		state: [],
		city: [],
		store_grade: [],
	},
	[discountGroupObjects.discount_value.key]: [],
	[discountGroupObjects.product_selection.key]: {
		discount_table: [],
		discount_barcode_table: [],
	},
	attach_store: false,
});

	const convertApiToState = (data, options = {}) => {
		const { isCopyMode = false } = options;
		const enrichDiscountValue = (discountArray) => {
			return (discountArray || []).map((discount) => {
				const clonedDiscount = isCopyMode
					? (() => {
							const { created_at, updated_at, discount_id, ...restDiscount } = discount || {};
							return restDiscount;
					  })()
					: discount;

				return {
					...clonedDiscount,
					display_string: generateSchemeStr([clonedDiscount], data.scheme_type) || "",
				};
			});
		};

		return{
		[discountGroupObjects.brand_channel.key]: {
			store_brand: data.brand || [],
			channel: data.channel || "",
		},

		[discountGroupObjects.general_information.key]: {
			scheme_type: data.scheme_type || "",
			scheme_name: data.scheme_name || "",
		},

		[discountGroupObjects.applicable_date.key]: {
			start_date: data.start_date ? jsDate(data.start_date) : "",
			end_date: data.end_date ? jsDate(data.end_date) : "",
			applicable_days: data.applicable_days || [],
		},

		[discountGroupObjects.store_availability.key]: {
			partner: data.partner || [],
			zone: data.zone || [],
			distributor: data.distributor || [],
			store: data.store || [],
			state: data.state || [],
			city: data.city || [],
			store_grade: data.store_grade || [],
		},

		[discountGroupObjects.discount_value.key]: enrichDiscountValue(data.discounts),

		[discountGroupObjects.product_selection.key]: {
			discount_table: [],
			discount_barcode_table: [],
		},
		attach_store: Boolean(data.attach_store),
		}};

function DiscountMaster() {
	const location = useLocation();
	const discountData = location.state?.data;
	const isCopyMode = location.state?.mode === "copy";

	const [controlledSteps, setControlledSteps] = useState(0)
	const [value, setValue] = useState(
		discountData === undefined
			? getInitialState()
			: convertApiToState(discountData, { isCopyMode })
	);
	const [storeData, setStoreData] = useState({});
	const [barcodeFile, setBarcodeFile] = useState("");
	const [open, setOpen] = useState(false);
	const [storeListOpen, setStoreListOpen] = useState(false);
	const [storeSearchTerm, setStoreSearchTerm] = useState("");
	const [finalSummary, setFinalSummary] = useState([]);
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const roleAccessData = roleAccess(user)
	
	const navigate = useNavigate();

	const prevStoreRef = useRef(
		snapshotStoreAvailability(value[discountGroupObjects.store_availability.key])
	);
	const prevBrandChannelRef = useRef({
		store_brand: [...(value[discountGroupObjects.brand_channel.key]?.store_brand || [])],
		channel: value[discountGroupObjects.brand_channel.key]?.channel || "",
	});

	const stpesNames = [discountGroupObjects.brand_channel.key,
		discountGroupObjects.store_availability.key,
		discountGroupObjects.product_selection.key,
	discountGroupObjects.applicable_date.key,
	discountGroupObjects.general_information.key,
discountGroupObjects.discount_value.key]

const cascadeOrder = [
	"partner",
	"distributor",
	"zone",
	"state",
	"city",
	"store_grade",
	"store",
];

	const getStores = async () => {
			try {
				// setDownloading(true);
				setLoadingOpen(true);
				await pythonVmInstance({
					url: `/discount-master/get`,
					headers: {
						"Content-Type": "application/json",
					},
					method: "GET",
				}).then((res) => {
					setStoreData(res?.data);
					discountData === undefined && setLoadingOpen(false);
				});
			} catch (error) {
				setSnackBarOpen(true);
				setLoadingOpen(false);
				setSnackBar({
					...snackBar,
					message: "Failed to load store master data",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			}
		};
	const getBarcodes = async () => {
			try {
				setLoadingOpen(true);
				const res = await pythonVmInstance({
					url: `/discount-master/get-barcodes`,
					params: { 
						channel: value?.[discountGroupObjects.brand_channel.key]?.channel, 
						barcode_url: discountData !== undefined ? discountData["barcode_file_url"] : null,
						discounted_ean_url: discountData !== undefined ? discountData["summary_file_url"] : null
					},
					headers: {
						"Content-Type": "application/json",
					},
					method: "GET",
				});
				
				setValue((prev) => ({
					...prev,
					[discountGroupObjects.product_selection.key]: {
						discount_table: res?.data?.["item_details"] || [],
						discount_barcode_table: res?.data?.["barcode_details"] || []
					}
				}));
				
				if (discountData?.final_summary) {
					setFinalSummary(discountData.final_summary);
				}
				
				setLoadingOpen(false);
			} catch (error) {
				setSnackBarOpen(true);
				setLoadingOpen(false);
				setSnackBar({
					...snackBar,
					message: "Failed to load barcode details",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			}
		};
	

	const isFilled = (data) => {
	if (data === null || data === undefined) return false;

	// ✅ Array validation
	if (Array.isArray(data)) {
		if (data.length === 0) return false;

		return data.every((item) => isFilled(item));
	}

	// ✅ Object validation
	if (typeof data === "object") {
		return Object.values(data).every((val) => isFilled(val));
	}

	// ✅ Primitive validation
	return data !== "";
};

const handleStepperChange = () => {
	let countSteps = 0;
	const channel = value[discountGroupObjects.brand_channel.key].channel;
	
	for (let i = 0; i < stpesNames.length; i++) {
		const key = stpesNames[i];
		const rawStepValue = value[key];
		let stepValue = rawStepValue;
		
		if (key === discountGroupObjects.store_availability.key && rawStepValue && typeof rawStepValue === "object") {
			stepValue = {
				...rawStepValue,
			};
			if (channel !== "MBO") {
				delete stepValue.distributor;
			}
			if (channel !== "LFS") {
				delete stepValue.partner;
			}
		}
		
		if (key === discountGroupObjects.product_selection.key) {
			const hasProducts = stepValue?.discount_table?.length > 0;
			if (!hasProducts) {
				countSteps = i;
				break;
			}
		} else {
			const allFilled = isFilled(stepValue);
			if (!allFilled) {
				countSteps = i;
				break;
			}
		}

		countSteps = i + 1;
	}

	setControlledSteps(countSteps);
};

 const handleDropDownMissBeheviour = () =>{
		const currentBrand = value[discountGroupObjects.brand_channel.key];
		const currentStore = value[discountGroupObjects.store_availability.key];
		const prevBrand = prevBrandChannelRef.current;
		const prevStore = prevStoreRef.current;

		const isBrandChanged = !arePrimitiveArraysEqual(prevBrand.store_brand || [], currentBrand.store_brand || []);
		const isChannelChanged = (prevBrand.channel || "") !== (currentBrand.channel || "");

		if (isBrandChanged || isChannelChanged) {
			setValue((prev) => ({
				...prev,
				[discountGroupObjects.store_availability.key]: {
					...getInitialState()[discountGroupObjects.store_availability.key]
				}
			}));
			prevBrandChannelRef.current = {
				store_brand: [...(currentBrand.store_brand || [])],
				channel: currentBrand.channel || "",
			};
			prevStoreRef.current = snapshotStoreAvailability();
			return;
		}

		const isStoreChanged = !arePrimitiveArraysEqual(prevStore.store || [], currentStore.store || []);

		// Store-first selection auto-fills zone/state/city/grade. Skip cascade clearing
		// so those parent fields are not wiped (and stores with them).
		if (!isStoreChanged) {
			let changedKey = null;
			for (const key of cascadeOrder) {
				if (!arePrimitiveArraysEqual(prevStore[key] || [], currentStore[key] || [])) {
					changedKey = key;
					break;
				}
			}

			if (changedKey && changedKey !== "store_grade") {
				const index = cascadeOrder.indexOf(changedKey);
				const updatedStore = { ...currentStore };
				for (let i = index + 1; i < cascadeOrder.length; i++) {
					updatedStore[cascadeOrder[i]] = [];
				}
				setValue((prev) => ({
					...prev,
					[discountGroupObjects.store_availability.key]: updatedStore
				}));
				prevStoreRef.current = snapshotStoreAvailability(updatedStore);
				prevBrandChannelRef.current = {
					store_brand: [...(currentBrand.store_brand || [])],
					channel: currentBrand.channel || "",
				};
				return;
			}
		}

		prevStoreRef.current = snapshotStoreAvailability(currentStore);
		prevBrandChannelRef.current = {
			store_brand: [...(currentBrand.store_brand || [])],
			channel: currentBrand.channel || "",
		};
 }

	const brandSelectionKey = useMemo(
		() => (value[discountGroupObjects.brand_channel.key]?.store_brand || []).join("|"),
		[value]
	);
	const storeAvailabilitySelectionKey = useMemo(
		() =>
			cascadeOrder
				.map((key) => (value[discountGroupObjects.store_availability.key]?.[key] || []).join("|"))
				.join("||"),
		[value]
	);
	const finalSummaryRefreshKey = useMemo(
		() =>
			JSON.stringify({
				scheme_name: value[discountGroupObjects.general_information.key]?.scheme_name || "",
				scheme_type: value[discountGroupObjects.general_information.key]?.scheme_type || [],
				start_date: value[discountGroupObjects.applicable_date.key]?.start_date || "",
				end_date: value[discountGroupObjects.applicable_date.key]?.end_date || "",
				applicable_days: value[discountGroupObjects.applicable_date.key]?.applicable_days || [],
				discount_value_count: value[discountGroupObjects.discount_value.key]?.length || 0,
				discount_barcode_count:
					value[discountGroupObjects.product_selection.key]?.discount_barcode_table?.length || 0,
			}),
		[value]
	);
	const stepperProgressKey = useMemo(
		() =>
			JSON.stringify({
				brand_channel: value[discountGroupObjects.brand_channel.key] || {},
				store_availability: value[discountGroupObjects.store_availability.key] || {},
				product_count: value[discountGroupObjects.product_selection.key]?.discount_table?.length || 0,
				date: value[discountGroupObjects.applicable_date.key] || {},
				general_information: value[discountGroupObjects.general_information.key] || {},
				discount_value_count: value[discountGroupObjects.discount_value.key]?.length || 0,
			}),
		[value]
	);

	useEffect(() => {
		if (Object.keys(storeData).length > 0) {
			handleDropDownMissBeheviour();
		}
	}, [
		brandSelectionKey,
		value[discountGroupObjects.brand_channel.key]?.channel,
		storeAvailabilitySelectionKey,
		storeData,
		discountData,
	]);

	useEffect(() => {
		handleStepperChange();
	}, [stepperProgressKey]);

	useEffect(() => {
		if (value[discountGroupObjects.discount_value.key].length === 0) {
			setFinalSummary([]);
		} else if (value[discountGroupObjects.product_selection.key]?.discount_barcode_table?.length > 0) {
			const timeoutId = window.setTimeout(() => {
				handleFinalSummary();
			}, 0);

			return () => window.clearTimeout(timeoutId);
		}
	}, [finalSummaryRefreshKey]);


	useEffect(() => {
		const loadData = async () => {
			await getStores();
			if (discountData !== undefined) {
				await getBarcodes();
			}
		};
		loadData();
		return () => {
			setValue(getInitialState());
			setFinalSummary([]);
			setBarcodeFile("");
		};
	}, []);
	useEffect(() => {
	if (!discountData) {
		setValue(getInitialState());
		setFinalSummary([]);
		setBarcodeFile("");
	}
}, [location.pathname]);

	const create_api_request = (form) =>{
		pythonVmInstance
				.post("/discount-master/create", form, { headers: { "Content-Type": "multipart/form-data" } })
				.then((res) => {
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Discount created successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
					navigate(-1)
				})
				.catch((err) => {
					const message = err?.response?.data?.message;
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: message ? message : "Failed to create discount",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
	}
	const update_api_request = (form) =>{
		form.append("id", discountData?.id);
		pythonVmInstance
				.put(`/discount-master/create`, form, { headers: { "Content-Type": "multipart/form-data" } })
				.then((res) => {
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Discount updated successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
					navigate(-1)
				})
				.catch((err) => {
					const message = err?.response?.data?.message;
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: message ? message : "Failed to update discount",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
	}

	const handleGetStoreData = async (channel, brand) => {
				try {
					setLoadingOpen(true);
					const params = new URLSearchParams({
									channel: channel,
									brand: brand,
								});
				const res = await pythonVmInstance.get(`/discount-master/discount-store-master?${params.toString()}`);
	
					return res?.data;
				} catch (err) {
					throw new Error("Failed to fetch store master data");
				}
			};

	const handleCheckStoreEmails = async (channel,brand,selectedStore = []) => {

	let stores = await handleGetStoreData(channel, brand);

	let missingEmail = [];

	const normalizedSelectedStores = selectedStore.map(normalizeValue);

	stores.forEach((store) => {

		// Match selected store
		const isSelectedStore = getStoreMatchValues(store).some((value) =>
			normalizedSelectedStores.includes(value)
		);

		if (!isSelectedStore) return;

		// Get all email related fields
		const emailFields = Object.keys(store).filter((key) => {
			const normalizedKey = key.toLowerCase();

			return (
				normalizedKey.includes("_email") ||
				normalizedKey.includes("email") ||
				normalizedKey.includes("mail")
			);
		});

		// Extract values
		const emailValues = emailFields
			.map((field) => store[field])
			.filter(Boolean);

		// Check if atleast one valid email exists
		const hasValidEmail = emailValues.some((email) => {

			if (Array.isArray(email)) {
				return email.some(
					(e) =>
						typeof e === "string" &&
						e.includes("@")
				);
			}

			return (
				typeof email === "string" &&
				email.includes("@")
			);
		});

		// No valid email found
		if (!hasValidEmail) {
			missingEmail.push(getStoreDisplayName(store));
		}
	});

	return missingEmail;
};

	const handleSave = async (from) => {
			// e.preventDefault();
			setLoadingOpen(true);

			const channel = value[discountGroupObjects.brand_channel.key]?.channel || '';
			const brand = value[discountGroupObjects.brand_channel.key]?.store_brand || [];
			const stores = value[discountGroupObjects.store_availability.key]?.store || [];

			const missingEmail = from === "submit" && await handleCheckStoreEmails(channel, brand, stores); 

			if (from === "submit" && missingEmail.length > 0) {
				setSnackBarOpen(true);

				setSnackBar({
					...snackBar,
					message: `Stores are missing email addresses: ${missingEmail.join(
						", "
					)}, please update the store master data before publishing.`,
					severity: "warning",
					anchorOrigin: {
						horizontal: "center",
						vertical: "top",
					},
				});

				setLoadingOpen(false);
				return;
			}
			let form = new FormData();
			if (value[discountGroupObjects.product_selection.key].discount_table?.length === 0) {
				// setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Please upload a barcode file and try again",
					severity: "warning",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
				return;
			}
			let discount_details = {}
			const selectedStoreCodes = getSelectedStoreCodesFromStoreDetails(
				value[discountGroupObjects.store_availability.key]?.store || [],
				allChannels
			);

			discount_details["brand"] = value[discountGroupObjects.brand_channel.key]?.store_brand || []
			discount_details["channel"] = value[discountGroupObjects.brand_channel.key]?.channel || ''
			discount_details["scheme_type"] = value[discountGroupObjects.general_information.key]?.scheme_type || ''
			discount_details["scheme_name"] = (value[discountGroupObjects.general_information.key]?.scheme_name || '').trim()
			discount_details["start_date"] = toMySQLDateTime(value[discountGroupObjects.applicable_date.key]?.start_date) || ''
			discount_details["end_date"] = toMySQLDateTime(
				getEndOfDayDate(value[discountGroupObjects.applicable_date.key]?.end_date)
			) || ''
			discount_details["applicable_days"] = value[discountGroupObjects.applicable_date.key]?.applicable_days || []
			// const enrichedDiscountValue = (value[discountGroupObjects.discount_value.key] || []).map(discount => ({
			// 	...discount,
			// 	display_string: generateSchemeStr([discount], value[discountGroupObjects.general_information.key]?.scheme_type) || ''
			// }));

			const enrichedDiscountValue = (
				value[discountGroupObjects.discount_value.key] || []
			).map((discount) => {
				const rowStoreCodes = getSelectedStoreCodesFromStoreDetails(
					discount?.store || [],
					storeDetails
				);

				const {
					updated_at,
					created_at,
					...restDiscount
				} = discount;

				return {
					...restDiscount,
					store_code: rowStoreCodes,

					display_string:
						generateSchemeStr(
							[restDiscount],
							value[
								discountGroupObjects
									.general_information.key
							]?.scheme_type
						) || "",
				};
			});

			discount_details["discount_value"] = enrichedDiscountValue;
			// discount_details["discount_table"] = value[discountGroupObjects.product_selection.key]?.discount_table || [];
			// discount_details["discount_barcode_table"] = value[discountGroupObjects.product_selection.key]?.discount_barcode_table || [];
			discount_details["city"] = value[discountGroupObjects.store_availability.key]?.city || []
			discount_details["state"] = value[discountGroupObjects.store_availability.key]?.state || []
			discount_details["zone"] = value[discountGroupObjects.store_availability.key]?.zone || []
			discount_details["distributor"] = value[discountGroupObjects.store_availability.key]?.distributor || []
			discount_details["partner"] = value[discountGroupObjects.store_availability.key]?.partner || []
			discount_details["store_grade"] = value[discountGroupObjects.store_availability.key]?.store_grade || []
			discount_details["store"] = value[discountGroupObjects.store_availability.key]?.store || []
			discount_details["store_code"] = selectedStoreCodes
			discount_details["submitted_by"] = from === "submit" ? user?.email : null
			discount_details["submitted_at"] = from === "submit" ? toMySQLDateTime(new Date()) : null
			discount_details["created_by"] = user?.email || null
			discount_details["updated_by"] = user?.email || null
			// discount_details["discount_str"] = finalSummary[0]?.scheme
			discount_details["updated_at"] = toMySQLDateTime(new Date())
			discount_details["status"] = from === "submit" ? "Submitted" : "Draft"
			discount_details["barcode_file_url"] = discountData !== undefined ? discountData["barcode_file_url"] : null
			discount_details["summary_file_url"] = discountData !== undefined ? discountData["summary_file_url"] : null
			discount_details["final_summary"] = serializeFinalSummary(finalSummary)
			discount_details["attach_store"] = Boolean(value?.attach_store)
			
			form.append("files", barcodeFile);
			form.append("discount_details", JSON.stringify(discount_details));
			form.append("from", from);
			if (isCopyMode) {
				form.append("mode", "copy");
				if (discountData?.id) {
					form.append("id", discountData.id);
				}
			}
			
			if (discountData !== undefined && !isCopyMode) {
				update_api_request(form)
			}
			else{
				create_api_request(form)
			}

		};

	const handleFileUpload = async (e) => {
			e.preventDefault();
			setLoadingOpen(true);
			let form = new FormData();
			if (barcodeFile === "") {
				// setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Please upload a barcode file and try again",
					severity: "warning",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
				return;
			}
	
			form.append("files", barcodeFile);
			form.append("email", user.email);
			form.append("channel", value?.[discountGroupObjects.brand_channel.key]?.channel);
			pythonVmInstance
				.post("/discount-master/upload-barcodes", form, { headers: { "Content-Type": "multipart/form-data" } })
				.then((res) => {
					setLoadingOpen(false);
					
					const itemDetails = res?.data?.item_details || [];
					const barcode_details = res?.data?.barcode_details || [];

					startTransition(() => {
						setValue((prev) => ({
							...prev,
							[discountGroupObjects.product_selection.key]: {
								...prev[discountGroupObjects.product_selection.key],
								discount_table: itemDetails,
								discount_barcode_table: barcode_details,
							},
						}));
					});

					// Selected brands
					const selectedBrands =
						value?.[discountGroupObjects.brand_channel.key]?.store_brand || [];

					// Exclude THOR and GUILD
					const allowedBrands = selectedBrands.filter(
						(brand) => !["THOR", "GUILD"].includes(brand?.toUpperCase())
					);

					// Uploaded brands from API response
					const uploadedBrands = [
						...new Set(
							itemDetails
								.map((item) => item?.brand?.trim()?.toUpperCase())
								.filter(Boolean)
						),
					];

					// Find unmatched brands
					const invalidBrands = uploadedBrands.filter(
						(brand) => !allowedBrands.map((b) => b?.toUpperCase()).includes(brand)
					);

					if (invalidBrands.length > 0) {
						setSnackBarOpen(true);
						setSnackBar({
							...snackBar,
							message: `Warning: Uploaded file contains invalid brands: ${invalidBrands.join(", ")}`,
							severity: "warning",
							anchorOrigin: { horizontal: "center", vertical: "top" },
						});

						return;
					}
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Barcode file uploaded successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
					
				})
				.catch((err) => {
					const message = err?.response?.data?.message;
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: message ? message : "Failed to upload barcode file",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
		};
	
	const handleFinalSummary = () => {
		const discountValueLookup = buildDiscountValueLookup(
			value[discountGroupObjects.discount_value.key] || []
		);
		const discountRowCache = new Map();
		const previousSummaryById = new Map(
			(finalSummary || []).map((row, index) => {
				const rowId = row?.BARCODE || row?.barcode || row?.Barcode || row?.bar_code || row?.EAN || row?.ean || row?.sku || row?.SKU || row?.id || `${row?.category || "row"}-${row?.option || index}-${index}`;
				return [rowId, row];
			})
		);
		const discounts = value[discountGroupObjects.discount_value.key] || [];
		const schemeTypes = value[discountGroupObjects.general_information.key]?.scheme_type;
		const firstDiscountArray = discounts.slice(0, 1);
		const initialScheme = generateSchemeStr(firstDiscountArray, schemeTypes) || '';
		const initialId = firstDiscountArray[0]?.id ? [firstDiscountArray[0].id] : [];

		const enrichDiscountValue = (discountArray) => {
			return discountArray.map(discount => ({
				...discount,
				display_string: generateSchemeStr([discount], schemeTypes) || ''
			}));
		};

		const matchDiscountIdsFromScheme = (schemeStr) => {
			if (!schemeStr) return [];

			const parts = String(schemeStr)
				.split(",")
				.map((part) => part.trim().toLowerCase())
				.filter(Boolean);

			if (!parts.length) return [];

			return discounts
				.filter((discount) => {
					const label = (
						discount?.display_string ||
						generateSchemeStr([discount], schemeTypes) ||
						""
					)
						.trim()
						.toLowerCase();
					return parts.includes(label);
				})
				.map((discount) => discount?.id)
				.filter(Boolean);
		};

		const getDiscountRowsFromIds = (discountValueIds = []) => {
			const cacheKey = (discountValueIds || []).join("|");

			if (discountRowCache.has(cacheKey)) {
				return discountRowCache.get(cacheKey);
			}

			const enrichedRows = enrichDiscountValue(
				(discountValueIds || [])
					.map((id) => discountValueLookup.get(id))
					.filter(Boolean)
			);

			discountRowCache.set(cacheKey, enrichedRows);
			return enrichedRows;
		};

		let data = value[discountGroupObjects.product_selection.key]?.discount_barcode_table.map(element => {
			const rowId = element?.BARCODE || element?.barcode || element?.Barcode || element?.bar_code || element?.EAN || element?.ean || element?.sku || element?.SKU || element?.id || `${element?.category || "row"}-${element?.option || ''}`;
			const previousRow = previousSummaryById.get(rowId);
			const schemeMatchedIds = matchDiscountIdsFromScheme(element?.scheme || previousRow?.scheme);
			const previousDiscountValueIds =
				previousRow?.discount_value_id ||
				(schemeMatchedIds.length ? schemeMatchedIds : initialId);
			const previousDiscountValues = previousRow?.discount_value?.length
				? enrichDiscountValue(previousRow.discount_value)
				: getDiscountRowsFromIds(previousDiscountValueIds);
			return {...element,
				// 'scheme': previousRow?.scheme || initialScheme,
				'discount_value_id': previousDiscountValueIds,
				'discount_value': previousDiscountValues.length > 0 ? previousDiscountValues : enrichDiscountValue(firstDiscountArray),
				'scheme_name': (value[discountGroupObjects.general_information.key]?.scheme_name || '').trim(),
				'date': `${value[discountGroupObjects.applicable_date.key]?.start_date || ''} to ${getEndOfDayDate(value[discountGroupObjects.applicable_date.key]?.end_date) || ''}`,
				'days': value[discountGroupObjects.applicable_date.key]?.applicable_days || [],
			}
		});
		startTransition(() => {
			setFinalSummary(data);
		});
	};

	const itemBrandOptions = useMemo(
		() =>
			Discount_Master_store_Brands.filter((brands) => roleAccessData?.brandList.includes(brands?.key)).map((b) => ({
				name: b?.key,
				key: b?.key,
			})),
		[roleAccessData?.brandList]
	);

	const channelOptions = useMemo(
		() =>
			DISOUNT_MASTER_CHANNEL_OPTIONS?.filter((channel) =>
				roleAccessData?.channelList.includes(channel["name"])
			),
		[roleAccessData?.channelList]
	);

	const allChannels = useMemo(
		() => [...(storeData?.ebo_stores || []), ...(storeData?.lfs_stores || []), ...(storeData?.mbo_stores || [])].flat(),
		[storeData]
	);

	const storeDetails = useMemo(() => {
		const selectedBrands = value?.[discountGroupObjects.brand_channel.key]?.store_brand || [];
		const selectedChannel = value?.[discountGroupObjects.brand_channel.key]?.channel;

		return allChannels?.filter((store) => {
			return (
				(store?.channel || "").trim() === (selectedChannel || "").trim() &&
				selectedBrands.includes((store?.brand || "").trim())
			);
		});
	}, [allChannels, value]);

	const selectedStartDate = value?.[discountGroupObjects.applicable_date.key]?.start_date;
	const today = new Date();
	const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const discountStartDate = selectedStartDate
		? new Date(
			selectedStartDate.getFullYear(),
			selectedStartDate.getMonth(),
			selectedStartDate.getDate()
		)
		: null;

	const isStartDateInvalidForSubmit = !discountStartDate || discountStartDate < todayStart;
	const isSubmitDisabled = finalSummary.length === 0 || controlledSteps < 5 || isStartDateInvalidForSubmit;
	const submitTooltipTitle = isStartDateInvalidForSubmit
		? "Submit is allowed only when the discount start date is today or later."
		: "";

	const selectedStoreLabels = useMemo(
		() =>
			(value?.[discountGroupObjects.store_availability.key]?.store || [])
				.map((store) => (typeof store === "object" ? getStoreDisplayName(store) : String(store || "").trim()))
				.filter(Boolean),
		[value]
	);

	const filteredSelectedStoreLabels = useMemo(
		() =>
			selectedStoreLabels.filter((store) =>
				store.toLowerCase().includes(storeSearchTerm.toLowerCase())
			),
		[selectedStoreLabels, storeSearchTerm]
	);

	return (
		<Box width="100%" pt={4}>
			<Box display="flex" flexDirection={"row"} width={"100%"} justifyContent={"space-between"}>
				<Box width={"65%"}>
					<Stepper orientation="vertical" nonLinear activeStep={controlledSteps}>
						<Step expanded completed={controlledSteps > 0}>
							<StepLabel>Brand and Channel Selection</StepLabel>
							<Box mt={2} mb={4}width={"90%"}>
								<BrandChannel value={value} setValue={setValue} disable={controlledSteps < 0} data={{"store_brand": itemBrandOptions, "channel":channelOptions }} />
							</Box>
						</Step>
						<Step expanded completed={controlledSteps > 1}>
							<StepLabel>Store Availability</StepLabel>
							<Box mt={2} mb={4} width={"90%"}>
								<StoreAvailabilitySection value={value} setValue={setValue} disable={controlledSteps < 1 } data = {storeDetails} />
								<Box sx={{ mt: 2, ml: 2, display: "flex", flexDirection: "column", rowGap: 1.5 }}>
									<Button
										variant="outlined"
										onClick={() => {
											setStoreSearchTerm("");
											setStoreListOpen(true);
										}}
										sx={{
											textTransform: "none",
											fontSize: "11px",
											borderRadius: "8px",
											width: "fit-content",
										}}
									>
										Selected Stores ({selectedStoreLabels.length})
									</Button>
									<Box
										sx={{
											px: 1.5,
											py: 1,
											border: "1px solid #e4e7ec",
											borderRadius: "10px",
											background: "#f8fafc",
											width: "fit-content",
											minWidth: 280,
										}}
									>
										<FormControlLabel
											control={
												<Switch
													size="small"
													disabled={controlledSteps < 1}
													checked={Boolean(value?.attach_store)}
													onChange={(event) =>
														setValue((prev) => ({
															...prev,
															attach_store: event.target.checked,
														}))
													}
													sx={{
														"& .MuiSwitch-switchBase": {
															color: "#d32f2f",
														},
														"& .MuiSwitch-track": {
															backgroundColor: "#ef9a9a",
															opacity: 1,
														},
														"& .MuiSwitch-switchBase.Mui-checked": {
															color: "#2e7d32",
														},
														"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
															backgroundColor: "#81c784",
															opacity: 1,
														},
														"& .MuiSwitch-switchBase.Mui-disabled": {
															color: "#e57373",
														},
														"& .MuiSwitch-switchBase.Mui-disabled.Mui-checked": {
															color: "#66bb6a",
														},
														"& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
															backgroundColor: "#ffcdd2",
															opacity: 1,
														},
														"& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track": {
															backgroundColor: "#c8e6c9",
															opacity: 1,
														},
													}}
												/>
											}
											label={
												<Box>
													<Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#101828" }}>
														Attach Store
													</Typography>
													<Typography sx={{ fontSize: "10px", color: "#667085" }}>
														Default is false. This value will be attach the stores in final email.
													</Typography>
												</Box>
											}
											sx={{ m: 0, alignItems: "flex-start" }}
										/>
									</Box>
								</Box>
							</Box>
						</Step>
						<Step expanded completed={controlledSteps > 2}>
							<StepLabel>Product Selection</StepLabel>
							<Box mt={2} mb={4} width={"90%"}>
								<ProductSelectionSection
									value={value}
									setValue={setValue}
									disable={controlledSteps < 2}
									onBlurEvent={getStores}
									barcodeFile={barcodeFile}
									setBarcodeFile={setBarcodeFile}
									handleFileUpload={handleFileUpload}
									table_data={value?.[discountGroupObjects.product_selection.key]?.discount_table}
								/>
							</Box>
						</Step>

						<Step expanded completed={controlledSteps > 3}>
							<StepLabel>Applicable Date</StepLabel>
							<Box mt={2} mb={4} width={"90%"}>
								<ApplicableDate value={value} setValue={setValue} disable={controlledSteps < 3} />
							</Box>
						</Step>
						<Step expanded completed={controlledSteps > 4}>
							<StepLabel>General Information</StepLabel>
							<Box mt={2} mb={4}>
								<MasterGeneralInformation value={value} setValue={setValue} disable={controlledSteps < 4} />
							</Box>
						</Step>
						<Step expanded completed={controlledSteps > 5}>
							<StepLabel>Discount Value</StepLabel>
							<Box mt={1} mb={4} width={"100%"}>
								<DiscountValueSection value={value} setValue={setValue}  disable={controlledSteps < 5} handleFinalSummary = {handleFinalSummary} setFinalSummary={setFinalSummary} storeDetails={storeDetails} />
							</Box>
						</Step>
					</Stepper>
				</Box>
				{/* <Box width={"52%"} display={"flex"} position={"sticky"} right={0} top={80} flexDirection={"column"} alignItems={"center"} rowGap={2}
				sx={{
						height: "calc(100vh - 100px)", // limit height relative to viewport
						overflowY: "auto",            // enable scroll inside
						// paddingRight: 2,              // optional scrollbar spacing
					}}
				>
					<DiscountOverview />
				</Box> */}
			</Box>
			{finalSummary.length > 0 && <Box>
				<FinalSummary
					discount_details={value}
					data={finalSummary}
					setData={setFinalSummary}
					discountValues={value[discountGroupObjects.discount_value.key] || []}
					schemeTypes={value[discountGroupObjects.general_information.key]?.scheme_type}
					isCopyMode={isCopyMode}
				/>
			</Box>}
			<Box mt={4} display="flex" justifyContent="flex-end" columnGap={2}>
				<Button variant="contained" onClick={() => handleSave( "save")} disabled={finalSummary.length === 0 || controlledSteps < 5}>
					Save
				</Button>
				<Tooltip title={submitTooltipTitle}>
					<span>
						<Button
							variant="contained"
							onClick={() => {
								setStoreSearchTerm("");
								setOpen(true);
							}}
							disabled={isSubmitDisabled}
						>
							Submit
						</Button>
					</span>
				</Tooltip>
			</Box>
			<Dialog open={storeListOpen} onClose={() => setStoreListOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Selected Stores ({selectedStoreLabels.length})</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						size="small"
						placeholder="Search selected store"
						value={storeSearchTerm}
						onChange={(e) => setStoreSearchTerm(e.target.value)}
						sx={{ mb: 1.5, mt: 0.5 }}
					/>
					<Box
						sx={{
							border: "1px solid #e4e7ec",
							borderRadius: "10px",
							background: "#f8fafc",
							p: 1.5,
							maxHeight: 320,
							overflowY: "auto",
						}}
					>
						{filteredSelectedStoreLabels.length > 0 ? (
							filteredSelectedStoreLabels.map((store, index) => (
								<Typography
									key={`${store}-${index}`}
									sx={{
										fontSize: "11px",
										color: "#344054",
										py: 0.5,
										borderBottom:
											index !== filteredSelectedStoreLabels.length - 1
												? "1px solid #eaecf0"
												: "none",
									}}
								>
									{store}
								</Typography>
							))
						) : (
							<Typography sx={{ fontSize: "11px", color: "#98a2b3" }}>
								No selected store found
							</Typography>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setStoreListOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Submit Discount</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: "12px", color: "#667085", mb: 2, mt: 0.5 }}>
						This action is irreversible. An approval request will be sent via email.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={() => {
							setOpen(false);
							handleSave("submit");
						}}
					>
						Submit
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default DiscountMaster;
